import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const staggerContainer = { visible: { transition: { staggerChildren: 0.1 } } };
const cardFadeIn = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { delay: 0.3, duration: 0.8 } } };
const quoteFadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };
const authorFadeIn = { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { delay: 0.2, duration: 0.5 } };

const TESTIMONIALS = [
  {
    quote: "Obsidian turned our honeymoon into something we'll talk about for the rest of our lives. The private volcano hike at sunrise, the hidden waterfall lunch — every moment was crafted with incredible attention to detail.",
    name: "James & Sarah W.",
    origin: "London, United Kingdom",
    experience: "Volcano & Coast Package",
    avatar: "https://images.unsplash.com/photo-1564998931274-8187168c5994?crop=entropy&cs=srgb&fm=jpg&w=120&h=120&fit=crop&q=80",
  },
  {
    quote: "As a diplomat stationed in Central America, discretion and quality are non-negotiable. Obsidian exceeded every expectation — seamless logistics, extraordinary locations, and a team that understands protocol without being asked.",
    name: "Henrik L.",
    origin: "Stockholm, Sweden",
    experience: "Executive Diplomatic Service",
    avatar: "https://images.unsplash.com/photo-1758599668974-6da5d7cc6cc4?crop=entropy&cs=srgb&fm=jpg&w=120&h=120&fit=crop&q=80",
  },
  {
    quote: "We've traveled extensively through Latin America, but El Salvador with Obsidian was a revelation. Our guide opened doors we never knew existed — from private coffee tastings to ancient ruins without another soul in sight.",
    name: "Philippe & Marie D.",
    origin: "Paris, France",
    experience: "Coffee & Culture Route",
    avatar: "https://images.unsplash.com/photo-1761472084994-61d80b8f4053?crop=entropy&cs=srgb&fm=jpg&w=120&h=120&fit=crop&q=80",
  },
  {
    quote: "The helicopter experience was beyond anything I've done in 30 years of adventure travel. Flying over volcanic craters with champagne in hand, followed by a private beach dinner — pure magic.",
    name: "Michael R.",
    origin: "New York, United States",
    experience: "Helicopter & Coast Experience",
    avatar: "https://images.unsplash.com/photo-1652948281656-61b6851cc163?crop=entropy&cs=srgb&fm=jpg&w=120&h=120&fit=crop&q=80",
  },
];

function TestimonialCard({ testimonial, index }) {
  return (
    <div className="relative border border-white/[0.06] bg-[#0A0A0A] p-8 md:p-14">
      <div className="absolute top-8 right-8 md:top-10 md:right-12">
        <Quote size={40} className="text-[#C5A059]/10" strokeWidth={1} />
      </div>
      <motion.p key={index} {...quoteFadeIn}
        className="font-serif text-white/80 text-xl md:text-2xl lg:text-3xl font-light leading-relaxed italic mb-10"
        data-testid="testimonial-quote">
        &ldquo;{testimonial.quote}&rdquo;
      </motion.p>
      <motion.div key={`author-${index}`} {...authorFadeIn} className="flex items-center gap-5">
        <img src={testimonial.avatar} alt={testimonial.name}
          className="w-12 h-12 rounded-full object-cover border border-white/10" data-testid="testimonial-avatar" />
        <div>
          <p className="font-sans text-white text-sm font-medium" data-testid="testimonial-name">{testimonial.name}</p>
          <p className="font-sans text-white/45 text-xs">{testimonial.origin}</p>
          <p className="font-sans text-[#C5A059]/60 text-[10px] tracking-[0.15em] uppercase mt-1">{testimonial.experience}</p>
        </div>
      </motion.div>
    </div>
  );
}

function NavArrow({ onClick, testId, children }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="w-10 h-10 border border-white/10 flex items-center justify-center text-white/60 hover:text-[#C5A059] hover:border-[#C5A059]/40 transition-all duration-300">
      {children}
    </button>
  );
}

export default function Testimonials() {
  const [current, setCurrent] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const animate = inView ? "visible" : "hidden";

  const prev = () => setCurrent(c => (c === 0 ? TESTIMONIALS.length - 1 : c - 1));
  const next = () => setCurrent(c => (c === TESTIMONIALS.length - 1 ? 0 : c + 1));

  return (
    <section data-testid="testimonials" className="py-24 md:py-32 lg:py-40 bg-[#050505] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div ref={ref} initial="hidden" animate={animate} variants={staggerContainer} className="text-center mb-16 md:mb-20">
          <motion.p variants={fadeUp} className="font-sans text-[#C5A059] text-[10px] md:text-xs tracking-[0.25em] uppercase mb-4">Guest Voices</motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-white text-4xl md:text-5xl font-light tracking-tight">
            Stories from<br className="hidden md:block" /><span className="italic"> Our Travelers</span>
          </motion.h2>
        </motion.div>

        <motion.div initial="hidden" animate={animate} variants={cardFadeIn} className="max-w-4xl mx-auto">
          <TestimonialCard testimonial={TESTIMONIALS[current]} index={current} />
          <div className="flex items-center justify-between mt-8">
            <div className="flex gap-2" data-testid="testimonial-dots">
              {TESTIMONIALS.map((t, i) => (
                <button key={t.name} onClick={() => setCurrent(i)}
                  className={`w-2 h-2 transition-all duration-500 ${i === current ? "bg-[#C5A059] w-6" : "bg-white/10 hover:bg-white/20"}`}
                  data-testid={`testimonial-dot-${i}`} />
              ))}
            </div>
            <div className="flex gap-3">
              <NavArrow onClick={prev} testId="testimonial-prev"><ChevronLeft size={16} strokeWidth={1.5} /></NavArrow>
              <NavArrow onClick={next} testId="testimonial-next"><ChevronRight size={16} strokeWidth={1.5} /></NavArrow>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
